import { useCallback, useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router';

import { ServiceFormTemplate, Step } from '@/widgets/ServiceFormTemplate';

import { Payment } from '@/features/ui';

import { useFormDraftPlainTools } from '@/hooks/index';
import { usePaymentShortPolling } from '@/hooks/usePaymentShortPolling';

import { FeatureContext } from '@/contexts/Features/FeatureContext';
import { ServicesContext } from '@/contexts/Services/ServicesContext';

import {
  FeatureName,
  FormDataType,
  UserRequest,
  UserRequestDraft,
  UserService,
  UserServicePaymentType,
} from '@/api/types';

import api from '@/api';

import { SERVICE_CODE, STEP_NUMBER_SIGN_UP, STEP_NUMBER_TO_PAY } from './lib/constants';
import { generatePayload } from './lib/generatePayload';
import {
  dataPreviewSchema,
  insuranceSelectionSchema,
  insuredAddressSchema,
  insuredDataSchema,
  insuredDocumentSchema,
} from './model/schemas';
import { InsuranceServiceMergedSchema, ServiceProps } from './model/types';
import {
  DataPreview,
  InsuranceSelection,
  InsuranceServiceResult,
  InsuredAddress,
  InsuredData,
  InsuredDocumentData,
  SmsVerification,
} from './ui/steps';

import './Insurance.scss';

export const STEPS: Array<Step<ServiceProps>> = [
  { title: 'Вибір страхового полісу', step: InsuranceSelection, validationSchema: insuranceSelectionSchema },
  { title: 'Особисті дані страхувальника', step: InsuredData, validationSchema: insuredDataSchema },
  { title: 'Адреса страхувальника', step: InsuredAddress, validationSchema: insuredAddressSchema },
  { title: 'Дані документа', step: InsuredDocumentData, validationSchema: insuredDocumentSchema },
  { title: 'Підтвердження даних', step: DataPreview, validationSchema: dataPreviewSchema },
  { title: 'Верифікація за СМС', step: SmsVerification },
  { title: 'Оплата', step: Payment },
  { title: 'Результат', step: InsuranceServiceResult },
];

export const Insurance: React.FC = () => {
  const { state: locationState } = useLocation();
  const { getServiceByCode } = useContext(ServicesContext);
  const { isFeatureAvailable } = useContext(FeatureContext);

  const currentService: UserService = getServiceByCode(SERVICE_CODE) as UserService;
  const isInsuranceOrderValid: boolean = isFeatureAvailable(FeatureName.BIA_INSURANCE);

  const {
    isLoading: isLoadingDraft,
    currentRequestId,
    userRequestDraft,
    onDraftCreate,
    onDraftSave,
    onDraftContinue,
    onPaymentContinue,
  } = useFormDraftPlainTools({
    serviceCode: SERVICE_CODE,
    stepNumberToPay: STEP_NUMBER_TO_PAY,
    stepNumberToSignUp: STEP_NUMBER_SIGN_UP,
    setData: (data) => {
      if (!isInsuranceOrderValid && !!data?.insuranceStartDate) {
        delete data?.insuranceStartDate;
      }

      setDefaultValues(data ?? {});
    },
    handleOnDraftContinue: async (stepNumberToOpen) => (isInsuranceOrderValid ? stepNumberToOpen : 1),
  });

  const [currentUserRequest, setCurrentUserRequest] = useState<Nullable<UserRequest>>(null);
  const { isShortPollingSuccessed, isShortPollingInProgress, isShortPollingFailed, startPaymentShortPolling } =
    usePaymentShortPolling(setCurrentUserRequest);

  const [defaultValues, setDefaultValues] = useState<FormDataType>({});
  const [redirectServiceInfo, setRedirectServiceInfo] = useState<{
    link: string;
    request_id: number;
  }>();

  useEffect(() => {
    // Redirect from other service to buy insurance
    if (!!locationState?.redirectServiceLink && !!locationState?.redirectServiceRequestId) {
      setRedirectServiceInfo({
        link: locationState.redirectServiceLink,
        request_id: locationState.redirectServiceRequestId,
      });
    }
  }, []);

  const onGercPaymentContinue = async (userRequestId: UserRequest['id']): Promise<number> => {
    if (sessionStorage.getItem('redirectServiceInfo')) {
      setRedirectServiceInfo(JSON.parse(sessionStorage.getItem('redirectServiceInfo') as string));
      sessionStorage.removeItem('redirectServiceInfo');
    }

    startPaymentShortPolling(userRequestId);

    return onPaymentContinue(userRequestId);
  };

  const handleCreateDraft = useCallback(
    async (data?: { stateForm: FormDataType }, specificStep?: (step: number) => void) => {
      return (
        specificStep &&
        onDraftCreate(
          {
            id: currentService.id,
            name: currentService.name,
            code: SERVICE_CODE,
            totalSteps: STEPS.length,
            currentStep: 1,
            lastStepFilled: 0,
          },
          specificStep,
          data,
        )
      );
    },
    [onDraftCreate, currentService.id, currentService.name, currentService.code, STEPS.length],
  );

  const createOrder = (formData: InsuranceServiceMergedSchema): ReturnType<typeof api.bia.createOrder> =>
    api.bia.createOrder(
      generatePayload({
        formData,
        requestId: (currentRequestId ?? userRequestDraft?.id)!,
        service: currentService,
      }),
    );

  const isLoading = isLoadingDraft;

  return (
    <ServiceFormTemplate
      className="insurance-service"
      serviceCode={SERVICE_CODE}
      title={currentService.name}
      steps={STEPS}
      isLoading={isLoading}
      defaultValues={defaultValues}
      currentRequestId={currentRequestId ?? null}
      skipAvailabilityValidation={(step) => step === 1}
      onDraftContinue={onDraftContinue}
      onGercPaymentContinue={onGercPaymentContinue}
      onPaymentContinue={onPaymentContinue}
      onDraftSave={onDraftSave}
      onDraftCreate={handleCreateDraft}
      stepProps={{
        invoiceStatus: currentUserRequest?.invoice?.status,
        redirectServiceInfo: redirectServiceInfo,
        paymentShortPolling: { isShortPollingInProgress },
        finishedUserRequestStatus: isShortPollingSuccessed || isShortPollingFailed,
        userRequestId: currentRequestId ?? currentUserRequest?.id ?? '',
        allowedPaymentType: currentService.paymentType ?? UserServicePaymentType.WITHOUT_PAYMENT,
        userRequestDraft: (userRequestDraft ??
          (currentRequestId ? ({ id: currentRequestId } as UserRequestDraft) : null)) as UserRequestDraft,
        createOrder,
        beforeRedirectToGerc: () => {
          if (redirectServiceInfo) {
            sessionStorage.setItem('redirectServiceInfo', JSON.stringify(redirectServiceInfo));
          }
        },
        shouldBeAbleToUpdateProfile: currentService.sync_profile_available ?? false,
      }}
    />
  );
};
